import { GridColDef } from "@mui/x-data-grid";
import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CustomLinkNavigate } from "../../../../SharedComponents/CustomLinkNavigate";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { ExploreHealthTooltipTexts } from "../../Common/HealthView.constants";
import { renderHeaderWithTooltip, renderScoreCell } from "../../Common/HealthView.common";

const HealthPurviewOverviewTable = (props: any) => {
  const isTPIDView = props.viewMode === "tpid";

  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{"Health Purview Overview details are not available."} </div>;
  };

  const columns: GridColDef[] = [
    ...(!isTPIDView ? [{
      headerName: "Tenant Name",
      field: "tenantName",
      sortable: false,
      flex: 1.5,
      minWidth: 200,
      renderHeader: (params: any) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.TenantName),
      renderCell: ({ row }: any) =>
        row.purview_HealthScore == null && row.purview_SupportScore == null ? (
          <span>{row.tenantName}</span>
        ) : (
          <CustomLinkNavigate to={navigateToTenant(row.tenantId)} text={row.tenantName} />
        )
    }] : []),
    {
      headerName: "TPID",
      field: "tpid",
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderHeader: (params: any) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.TPID)
    },
    {
      headerName: "TPAccountName",
      field: "tpAccountName",
      flex: 1.5,
      minWidth: 250,
      sortable: true,
      renderHeader: (params: any) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.TPAccountName),
      ...(isTPIDView ? {
        renderCell: ({ row }: any) =>
          row.purview_HealthScore == null && row.purview_SupportScore == null ? (
            <span>{row.tpAccountName}</span>
          ) : (
            <span>{row.tpAccountName}</span>
          )
      } : {})
    },
    {
      headerName: "ExpirationDate",
      field: "expirationDate",
      flex: 1.5,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.ExpirationDate)
    },
    {
      headerName: "Customer Health Score",
      field: "purview_HealthScore",
      type: "number",
      flex: 1.2,
      minWidth: 250,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CustomerHealthScore)
    },
    {
      headerName: "SHI Score",
      field: "purview_SupportScore",
      type: "number",
      flex: 1.2,
      minWidth: 160,
      sortable: true,
      headerAlign: "center",
      align: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.SHIScore)
    },
    {
      headerName: "Mission Critical Services",
      field: "purview_MissionCriticalServices",
      flex: 1,
      minWidth: 270,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.MissionCritical),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.purview_MissionCriticalServices === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Intensive Care",
      field: "intensiveCare",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.IntensiveCare),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.intensiveCare === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Purview BwC",
      field: "purviewBwC",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.PurviewBwC),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.purviewBwC === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Active SR",
      type: "number",
      field: "purview_ActiveSupportRequests",
      flex: 1,
      minWidth: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.ActiveSR)
    },
    {
      headerName: "Critical Cases",
      type: "number",
      field: "purview_CritSitCases",
      flex: 1,
      minWidth: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CriticalCases)
    },
    {
      headerName: "UAT",
      type: "number",
      field: "purview_UATFeatureRequests",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.UAT)
    },
    {
      headerName: "DCR",
      type: "number",
      field: "purview_DCRFeatureRequests",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.DCR)
    },
    {
      headerName: "CRM",
      type: "number",
      field: "purview_CRMFeatureRequests",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CRM)
    },
    {
      headerName: "Audit Health Score",
      field: "purview_HealthScore_Audit",
      type: "number",
      flex: 1.2,
      minWidth: 230,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.AuditHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_Audit}
        </div>
      )
    },
    {
      headerName: "CC Health Score",
      field: "purview_HealthScore_CC",
      type: "number",
      flex: 1.2,
      minWidth: 230,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CCHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_CC}
        </div>
      )
    },
    {
      headerName: "CM Health Score",
      field: "purview_HealthScore_CM",
      type: "number",
      flex: 1.2,
      minWidth: 230,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CMHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_CM}
        </div>
      )
    },
    {
      headerName: "DLM Health Score",
      field: "purview_HealthScore_DLM",
      type: "number",
      flex: 1.2,
      minWidth: 230,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.DLMHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_DLM}
        </div>
      )
    },
    {
      headerName: "DLP Health Score",
      field: "purview_HealthScore_DLP",
      type: "number",
      flex: 1.2,
      minWidth: 230,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.DLPHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_DLP}
        </div>
      )
    },
    {
      headerName: "eDiscovery Health Score",
      field: "purview_HealthScore_eDiscovery",
      type: "number",
      flex: 1.2,
      minWidth: 250,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.eDiscoveryHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_eDiscovery}
        </div>
      )
    },
    {
      headerName: "IRM Health Score",
      field: "purview_HealthScore_IRM",
      type: "number",
      flex: 1.2,
      minWidth: 230,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.IRMHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_IRM}
        </div>
      )
    },
    {
      headerName: "MIP Health Score",
      field: "purview_HealthScore_MIP",
      type: "number",
      flex: 1.2,
      minWidth: 230,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.MIPHealthScore),
      renderCell: ({ row }: any) => (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {row.purview_HealthScore_MIP}
        </div>
      )
    }
  ];

  const scoreStatusByField: Record<string, string> = {
    purview_HealthScore: "purview_CustomerHealthScoreStatus",
    purview_SupportScore: "purview_SupportHealthScoreStatus"
  };

  const columnsWithScoreFormatting = columns.map((column) => {
    if (typeof column.field !== "string" || !column.field.toLowerCase().includes("score")) {
      return column;
    }

    return {
      ...column,
      cellClassName: "score-cell",
      renderCell: ({ row }: any) =>
        renderScoreCell(
          row[column.field],
          scoreStatusByField[column.field] ? row[scoreStatusByField[column.field]] : undefined
        )
    };
  });

  const tickColumnFields = new Set(["purview_MissionCriticalServices", "intensiveCare", "purviewBwC"]);
  const keyColumnFields = ["tenantName", "tpid", "tpAccountName", "expirationDate"];
  const scoreColumns = columnsWithScoreFormatting.filter((column) => typeof column.field === "string" && column.field.toLowerCase().includes("score"));
  const tickColumns = columnsWithScoreFormatting.filter((column) => typeof column.field === "string" && tickColumnFields.has(column.field));
  const otherColumns = columnsWithScoreFormatting.filter(
    (column) =>
      typeof column.field === "string" &&
      !keyColumnFields.includes(column.field) &&
      !column.field.toLowerCase().includes("score") &&
      !tickColumnFields.has(column.field)
  );

  const orderedColumns = [
    ...columnsWithScoreFormatting.filter((column) => typeof column.field === "string" && keyColumnFields.includes(column.field)),
    ...scoreColumns,
    ...tickColumns,
    ...otherColumns
  ];

  const columnsForCompactView = orderedColumns.map((column) => {
    const field = typeof column.field === "string" ? column.field : "";
    if (field === "tenantName") {
      return {
        ...column,
        flex: 0.95,
        minWidth: 140
      };
    }

    if (field === "tpAccountName") {
      return {
        ...column,
        flex: 1,
        minWidth: 160
      };
    }

    if (field === "tpid") {
      const { flex, ...columnWithoutFlex } = column;
      return {
        ...columnWithoutFlex,
        width: 72,
        minWidth: 72,
        maxWidth: 72
      };
    }

    if (field === "expirationDate") {
      return {
        ...column,
        flex: 0.8,
        minWidth: 110
      };
    }

    if (tickColumnFields.has(field)) {
      const { flex, ...columnWithoutFlex } = column;
      return {
        ...columnWithoutFlex,
        width: 78,
        minWidth: 78,
        maxWidth: 78
      };
    }

    if (column.type === "number") {
      const { flex, ...columnWithoutFlex } = column;
      return {
        ...columnWithoutFlex,
        width: 92,
        minWidth: 92,
        maxWidth: 92
      };
    }

    return {
      ...column,
      flex: 0.8,
      minWidth: 110
    };
  });

  const tableColumns = columnsForCompactView;

  React.useEffect(() => {
    setTimeout(() => {
      const elements = document.getElementsByClassName("MuiDataGrid-virtualScroller");
      Array.from(elements).forEach(function (element) {
        element.setAttribute("tabIndex", "0");
      });
    }, 1000);
  }, []);

  return (
    <>
      {props.tableData && props.tableData.length > 0 ? (
        <div style={{ height: 600, width: "100%" }} tabIndex={0}>
          <CustomDataGrid
            columns={tableColumns}
            columnHeaderHeight={112}
            enableUrlParams
            exportCsvButtonProps={{
              fileNamePrefix: "support-requests"
            }}
            getRowId={(row: any) => isTPIDView ? row.tpid : row.tenantId}
            rows={{ values: props.tableData ?? [], count: props.tableData?.length ?? 0 }}
            showCellVerticalBorder
            sx={{
              "& .MuiDataGrid-cell.score-cell": {
                padding: "0 !important"
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                justifyContent: "center"
              },
              "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                justifyContent: "center",
                overflow: "visible"
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: "1.2",
                textOverflow: "clip",
                overflow: "visible"
              }
            }}
            kustoLink={props?.kustoLink}
          />
        </div>
      ) : (
        showNoData() || ""
      )}
    </>
  );
};

function navigateToTenant(tenantId: string) {
  return `/customer?id=${tenantId}&tab=customerHealth&section=Purview&from=explorehealth`;
}

export default HealthPurviewOverviewTable;
